import { useState, useEffect } from "react";
import { RotateCcw, X } from "lucide-react";
import { apiRequest } from "../context/AuthContext";

const DISMISS_KEY = id => `cb_undo_dismissed_${id}`;

/**
 * Slim dismissable, purely informational banner shown for 30 days after a
 * year switch. Reverting a switch is admin-only — this banner has no action
 * of its own, it just tells the player the window exists and how to reach
 * support. Dismissed state is persisted in localStorage per switch ID so it
 * survives page reloads but resets for each new switch.
 */
export default function SwitchUndoBanner({ userId, refreshTrigger }) {
  const [sw, setSw]           = useState(null);   // the latest revert-eligible switch
  const [daysLeft, setDaysLeft] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setSw(null);      // clear stale state on every refresh
    setDismissed(false);
    apiRequest("get", `/players/${userId}/enrolment-year-switches`)
      .then(({ data }) => {
        const eligible = (data || []).find(s => s.revert_eligible && !s.reverted);
        if (!eligible) return;
        if (localStorage.getItem(DISMISS_KEY(eligible.id))) return; // already dismissed
        const deadline = new Date(eligible.revert_deadline);
        const days = Math.max(0, Math.ceil((deadline - Date.now()) / 86_400_000));
        setSw(eligible);
        setDaysLeft(days);
      })
      .catch(() => {});
  }, [userId, refreshTrigger]);

  const dismiss = () => {
    if (sw) localStorage.setItem(DISMISS_KEY(sw.id), "1");
    setDismissed(true);
  };

  if (!sw || dismissed) return null;

  const switchedDate = sw.switched_at
    ? new Date(sw.switched_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : "recently";

  return (
    <div
      data-testid="switch-undo-banner"
      className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
    >
      <RotateCcw className="w-4 h-4 text-amber-500 flex-shrink-0" />
      <p className="flex-1 text-sm text-amber-800">
        You switched to{" "}
        <strong className="font-bold">{sw.new_target_year}</strong> on {switchedDate}.{" "}
        Email{" "}
        <a href="mailto:support@getcourtbound.com" className="underline hover:text-amber-900">
          support@getcourtbound.com
        </a>{" "}
        within{" "}
        <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong> if you'd like this reverted.
      </p>
      <button
        data-testid="switch-undo-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-amber-400 hover:text-amber-600 transition-colors flex-shrink-0 p-0.5 rounded"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
